import {
  Box, Heading, Text, Spinner, Flex, Tabs, TabList, TabPanels, Tab, TabPanel,
  useColorModeValue,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { fetchVocabulary, type VocabularyKind } from '../../api/dictionariesApi';
import { VocabularySection } from './components/VocabularySection';
import { LicensesSection } from './components/LicensesSection';

// Genres/moods/tags are EXTRACTED from track metadata (free-text collected across the library),
// NOT the onboarding-wizard lookup tables. Tempo is intentionally omitted — track tempo is
// typically a numeric BPM extracted from the file, not a curatable vocabulary.
const FREE_TEXT_KINDS: { kind: VocabularyKind; labelKey: string }[] = [
  { kind: 'genre', labelKey: 'admin.dictionary.tabs.genres' },
  { kind: 'mood', labelKey: 'admin.dictionary.tabs.moods' },
  { kind: 'tag', labelKey: 'admin.dictionary.tabs.tags' },
];

/**
 * Dictionary Manager (006 FR-079..FR-086): one place to rename / merge / delete the shared
 * vocabularies used across the library. Genres, moods, and tags are collected free-text values
 * extracted from track metadata; licenses are a managed database list (add/rename/merge/delete).
 * Each change propagates to every track using the value.
 */
export function DictionaryManagerPage() {
  const { t } = useTranslation();
  const textColor = useColorModeValue('navy.700', 'white');
  const secondaryColor = useColorModeValue('secondaryGray.600', 'secondaryGray.500');

  const { data, isLoading } = useQuery({ queryKey: ['admin-vocabulary'], queryFn: fetchVocabulary });

  const valuesByKind: Record<VocabularyKind, { value: string; trackCount: number }[]> = {
    genre: data?.genres ?? [],
    mood: data?.moods ?? [],
    tempo: data?.tempos ?? [],
    tag: data?.tags ?? [],
  };

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }} data-testid="dictionary-manager-page">
      <Heading size="md" mb={1} color={textColor}>{t('admin.dictionary.title')}</Heading>
      <Text color={secondaryColor} fontSize="sm" mb={5}>{t('admin.dictionary.subtitle')}</Text>

      {isLoading ? (
        <Flex justify="center" p={8}><Spinner size="xl" /></Flex>
      ) : (
        <Tabs variant="soft-rounded" colorScheme="brand" isLazy>
          <TabList flexWrap="wrap" gap={2} mb={4}>
            {FREE_TEXT_KINDS.map(({ kind, labelKey }) => (
              <Tab key={kind} data-testid={`dictionary-tab-${kind}`}>
                {t(labelKey)} ({valuesByKind[kind].length})
              </Tab>
            ))}
            <Tab data-testid="dictionary-tab-licenses">{t('admin.dictionary.tabs.licenses')}</Tab>
          </TabList>
          <TabPanels>
            {FREE_TEXT_KINDS.map(({ kind }) => (
              <TabPanel key={kind} px={0}>
                <VocabularySection kind={kind} values={valuesByKind[kind]} />
              </TabPanel>
            ))}
            <TabPanel px={0}>
              <LicensesSection />
            </TabPanel>
          </TabPanels>
        </Tabs>
      )}
    </Box>
  );
}
